import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SlidePanel } from '@/components/ui/slide-panel';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';
import { Team, Manager, Role } from '@/types';
import { creatableTeamTypeOptions } from '@/lib/teamTypes';
import { Loader2 } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionContext';

interface TeamFormProps {
  initialData?: Team;
  onSubmit: (data: Omit<Team, 'id'>) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  managers: Manager[];
  roles: Role[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface NewManagerData {
  firstName: string;
  lastName: string;
  email: string;
  role_id: string;
  phone: string;
  password: string;
  managerId: string;
  commercial_or_residential: string;
}

/**
 * Undo a manager account this form just created, when a later step failed.
 *
 * ⚠️ THIS USED TO BE `supabase.from('users').delete().eq('id', managerId)` FROM
 * THE BROWSER, and it was the only cross-user write in the product that RLS
 * actually governed. It worked because `users_delete` carried no role predicate
 * at all — any authenticated user could delete any user row in their own tenant,
 * which was F-19. `20260812u` closed that, so the raw delete would now fail for
 * anyone without `manage_settings` or `user.manage_all` and leave the orphan
 * account behind silently.
 *
 * Routed through `delete-admin-user` instead, which holds service_role and does
 * its own authorization (requireCaller + requireRole(['Admin']) +
 * requireTargetUser + requireRoleAssignable). Two things improve besides the
 * permission: it uses `hard_delete_user`, which clears roughly 120 foreign-key
 * references the bare DELETE would have tripped over on a user who already has
 * a team, and the deletion is now audited like every other one.
 *
 * ⚠️ FAILURE IS REPORTED, NOT SWALLOWED. A rollback that quietly does nothing
 * leaves an account nobody knows about. If this cannot clean up, the caller is
 * told the id so somebody can.
 */
async function rollbackCreatedManager(managerId: string, because: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('delete-admin-user', {
      body: { id: managerId, hard_delete: true },
    });
    if (error) throw error;
  } catch (err) {
    console.error(
      `[TeamForm] Could not roll back the manager account ${managerId} after ${because}. ` +
        'It still exists and needs removing by hand:',
      err,
    );
  }
}

export function TeamForm({ initialData, onSubmit, onCancel, isSubmitting, managers, roles, open, onOpenChange }: TeamFormProps) {
  const [formData, setFormData] = useState<Omit<Team, 'id'>>({
    name: '',
    type: 'SALES',
    manager_id: '',
    biz_dev_id: '',
    manager_name: '',
    biz_dev_rep_name: '',
  });
  const [isCreatingManager, setIsCreatingManager] = useState(false);
  const [newManager, setNewManager] = useState<NewManagerData>({
    firstName: '',
    lastName: '',
    email: '',
    role_id: '',
    phone: '',
    password: '',
    managerId: '',
    commercial_or_residential: 'residential'
  });
  const [isManagerPhoneValid, setIsManagerPhoneValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  // Initialize and update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        type: initialData.type || 'SALES',
        manager_id: initialData.manager_id || '',
        biz_dev_id: initialData.biz_dev_id || '',
        manager_name: initialData.manager_name || '',
        biz_dev_rep_name: initialData.biz_dev_rep_name || '',
      });
    } else {
      setFormData({
        name: '',
        type: 'SALES',
        manager_id: '',
        biz_dev_id: '',
        manager_name: '',
        biz_dev_rep_name: '',
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }
    if (isCreatingManager) {
      if (!newManager.firstName) newErrors.firstName = 'First name is required';
      if (!newManager.lastName) newErrors.lastName = 'Last name is required';
      if (!newManager.email) newErrors.email = 'Email is required';
      if (!newManager.role_id) newErrors.role_id = 'Role is required';
      if (!newManager.password) newErrors.password = 'Password is required';
      if (newManager.phone && !/^\d{10}$/.test(newManager.phone)) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
        setIsManagerPhoneValid(false);
      } else {
        setIsManagerPhoneValid(true);
      }
      if (!newManager.commercial_or_residential) newErrors.commercial_or_residential = 'Organization type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to perform this action.',
          variant: 'destructive',
        });
        return;
      }

      const currentUserId = session.data.session.user.id;

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role_id')
        .eq('id', currentUserId)
        .single();
      if (userError) {
        throw new Error('Failed to fetch user role: ' + userError.message);
      }

      const { data: roleData, error: roleError } = await supabase
        .from('roles')
        .select('id')
        .eq('name', 'Biz Dev Rep')
        .single();
      if (roleError && roleError.code !== 'PGRST116') {
        throw new Error('Failed to fetch Biz Dev Rep role: ' + roleError.message);
      }

      // Check if user is Biz Dev Rep via permission system
      const isBizDevRep = userData?.role_id === roleData?.id;

      if (isCreatingManager) {
        // Create manager via signup-organization edge function
        const createManagerData = {
          firstName: newManager.firstName,
          lastName: newManager.lastName,
          email: newManager.email,
          role_id: newManager.role_id,
          phone: newManager.phone,
          password: newManager.password,
          managerId: newManager.managerId,
          organization: formData.name,
          commercial_or_residential: newManager.commercial_or_residential
        };

        const functionsBaseUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '/functions/v1';
        const response = await fetch(`${functionsBaseUrl}/signup-organization`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session.access_token}`
          },
          body: JSON.stringify(createManagerData)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create manager user.');
        }

        const managerId = data.user.id;

        // Fetch the team created by the edge function
        const { data: teamData, error: teamError } = await supabase
          .from('teams')
          .select('id')
          .eq('name', formData.name)
          .eq('manager_id', managerId)
          .single();
        if (teamError || !teamData) {
          await rollbackCreatedManager(managerId, 'the team it created could not be read back');
          throw new Error('Failed to fetch team: ' + (teamError?.message || 'Team not found'));
        }

        // Update team with Biz Dev Rep ID if applicable
        if (isBizDevRep || formData.biz_dev_id) {
          const bizDevId = isBizDevRep ? currentUserId : (formData.biz_dev_id === 'none' ? null : formData.biz_dev_id);
          const { error: updateError } = await supabase
            .from('teams')
            .update({
              biz_dev_id: bizDevId
            })
            .eq('id', teamData.id);
          if (updateError) {
            await rollbackCreatedManager(managerId, 'the team could not be linked to its Biz Dev Rep');
            await supabase.from('teams').delete().eq('id', teamData.id);
            throw new Error('Failed to update team with Biz Dev Rep ID: ' + updateError.message);
          }

          // Assign Biz Dev Rep to team if not already assigned by edge function
          if (bizDevId) {
            const { error: userTeamError } = await supabase
              .from('user_teams')
              .insert({
                user_id: bizDevId,
                team_id: teamData.id
              });
            if (userTeamError) {
              toast({
                title: 'Warning',
                description: 'Failed to assign Biz Dev Rep to team: ' + userTeamError.message,
                variant: 'default',
              });
            }
          }
        }

        // Reset form and close dialog
        setIsCreatingManager(false);
        setNewManager({
          firstName: '',
          lastName: '',
          email: '',
          role_id: '',
          phone: '',
          password: '',
          managerId: '',
                commercial_or_residential: 'residential'
        });
        setFormData({
          name: '',
          type: 'SALES',
          manager_id: '',
          biz_dev_id: '',
          manager_name: '',
          biz_dev_rep_name: '',
        });
        onOpenChange(false);
        toast({
          title: 'Success',
          description: 'Team and manager created successfully!',
        });
      } else {
        // Create team without manager
        const { manager_name, biz_dev_rep_name, ...submitData } = formData;
        await onSubmit(submitData);
        setFormData({
          name: '',
          type: 'SALES',
          manager_id: '',
          biz_dev_id: '',
          manager_name: '',
          biz_dev_rep_name: '',
        });
        onOpenChange(false);
        toast({
          title: 'Success',
          description: 'Team created successfully!',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create/update team.',
        variant: 'destructive',
      });
    }
  };



  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value === 'none' ? '' : value
    }));
  };

  const handleManagerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewManager(prev => ({
      ...prev,
      [name === 'phone_number' ? 'phone' : name]: value
    }));
    if (name === 'phone_number') {
      const phoneRegex = /^\d{10}$/;
      setIsManagerPhoneValid(value === '' || phoneRegex.test(value));
    }
  };

  const handleManagerSelectChange = (name: string, value: string) => {
    setNewManager(prev => ({ ...prev, [name]: value }));
  };

  return (
    <SlidePanel
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={initialData ? 'Edit Team' : 'Add Team'}
      width="lg"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
      footer={
        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !formData.name || (isCreatingManager && Object.keys(errors).length > 0)}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {initialData ? 'Updating...' : 'Creating...'}
              </>
            ) : initialData ? (
              'Update Team'
            ) : (
              'Create Team'
            )}
          </Button>
        </div>
      }
    >
      <div className="text-sm text-muted-foreground mb-6">
        {initialData ? 'Update team information.' : 'Create a new team for your organization.'}
      </div>

      <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="flex flex-row items-center gap-0.5">
            <Label htmlFor="name" className="w-32 min-w-[120px] text-right">Team Name *</Label>
            <div className="flex-1">
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter team name"
                disabled={isSubmitting}
                className="w-full"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>
          </div>
          
          <div className="flex flex-row items-center gap-0.5">
            <Label htmlFor="type" className="w-32 min-w-[120px] text-right">Team Type *</Label>
            <div className="flex-1">
              <SearchableSelect
                id="type"
                value={formData.type}
                onValueChange={(value) => handleSelectChange('type', value)}
                options={creatableTeamTypeOptions(initialData?.type)}
                placeholder="Select team type"
                disabled={isSubmitting}
                className="w-full"
                inDialog={false}
              />
            </div>
          </div>
          
          <div className="flex flex-row items-center gap-0.5">
            <Label htmlFor="manager_id" className="w-32 min-w-[120px] text-right">Manager</Label>
            <div className="flex-1">
              <SearchableSelect
                id="manager_id"
                value={formData.manager_id || 'none'}
                onValueChange={(value) => handleSelectChange('manager_id', value)}
                options={[
                  { label: 'None', value: 'none' },
                  ...managers.map(manager => ({
                    label: `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email || 'Unknown Manager',
                    value: manager.id.toString()
                  }))
                ]}
                placeholder="Select manager"
                disabled={isSubmitting || isCreatingManager}
                className="w-full"
                inDialog={false}
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreatingManager(!isCreatingManager)}
              disabled={isSubmitting}
            >
              {isCreatingManager ? 'Cancel New Manager' : 'Add New Manager'}
            </Button>
          </div>

          {isCreatingManager && (
            <div className="grid gap-4 border-t pt-4 mt-2">
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="firstName" className="w-32 min-w-[120px] text-right">First Name *</Label>
                <div className="flex-1">
                  <Input
                    id="firstName"
                    name="firstName"
                    value={newManager.firstName}
                    onChange={handleManagerInputChange}
                    className="w-full"
                  />
                  {errors.firstName && (
                    <p className="text-sm text-destructive">{errors.firstName}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="lastName" className="w-32 min-w-[120px] text-right">Last Name *</Label>
                <div className="flex-1">
                  <Input
                    id="lastName"
                    name="lastName"
                    value={newManager.lastName}
                    onChange={handleManagerInputChange}
                    className="w-full"
                  />
                  {errors.lastName && (
                    <p className="text-sm text-destructive">{errors.lastName}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="email" className="w-32 min-w-[120px] text-right">Email *</Label>
                <div className="flex-1">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={newManager.email}
                    onChange={handleManagerInputChange}
                    className="w-full"
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email}</p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="role_id" className="w-32 min-w-[120px] text-right">Role *</Label>
                <div className="flex-1">
                  <SearchableSelect
                    id="role_id"
                    value={newManager.role_id}
                    onValueChange={(value) => handleManagerSelectChange('role_id', value)}
                    options={roles.map(role => ({
                      label: role.name,
                      value: role.id.toString()
                    }))}
                    placeholder="Select role"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                  {errors.role_id && (
                    <p className="text-sm text-destructive">{errors.role_id}</p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="phone" className="w-32 min-w-[120px] text-right">Phone</Label>
                <div className="flex-1">
                  <Input
                    id="phone"
                    name="phone"
                    value={newManager.phone}
                    onChange={handleManagerInputChange}
                    placeholder="1234567890"
                    className="w-full"
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive">{errors.phone}</p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="password" className="w-32 min-w-[120px] text-right">Password *</Label>
                <div className="flex-1">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={newManager.password}
                    onChange={handleManagerInputChange}
                    className="w-full"
                  />
                  <div className="flex items-center mt-1">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      {showPassword ? 'Hide' : 'Show'} password
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-destructive">{errors.password}</p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="managerId" className="w-32 min-w-[120px] text-right">Thryve Manager ID</Label>
                <div className="flex-1">
                  <Input
                    id="managerId"
                    name="managerId"
                    value={newManager.managerId}
                    onChange={handleManagerInputChange}
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="flex flex-row items-center gap-0.5">
                <Label htmlFor="commercial_or_residential" className="w-32 min-w-[120px] text-right">Org Type *</Label>
                <div className="flex-1">
                  <SearchableSelect
                    id="commercial_or_residential"
                    value={newManager.commercial_or_residential}
                    onValueChange={(value) => handleManagerSelectChange('commercial_or_residential', value)}
                    options={[
                      { label: 'Residential', value: 'residential' },
                      { label: 'Commercial', value: 'commercial' }
                    ]}
                    placeholder="Select organization type"
                    className="w-full"
                    inDialog={false}
                  />
                  {errors.commercial_or_residential && (
                    <p className="text-sm text-destructive">{errors.commercial_or_residential}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-row items-center gap-0.5">
            <Label htmlFor="biz_dev_id" className="w-32 min-w-[120px] text-right">Biz Dev Rep</Label>
            <div className="flex-1">
              <SearchableSelect
                id="biz_dev_id"
                value={formData.biz_dev_id || 'none'}
                onValueChange={(value) => handleSelectChange('biz_dev_id', value)}
                options={[
                  { label: 'None', value: 'none' },
                  ...managers.map(manager => ({
                    label: `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email || 'Unknown Manager',
                    value: manager.id.toString()
                  }))
                ]}
                placeholder="Select biz dev rep"
                disabled={isSubmitting}
                className="w-full"
                inDialog={false}
              />
            </div>
          </div>
        </form>
    </SlidePanel>
  );
}

//test